import type { Localizations, Translations } from '.';
import type { ContinentItem, CountryItem } from './data';
import { catalogTranslations } from './generated/catalog-frontend/translations';
import { hubTranslations } from './generated/hub-frontend/translations';
import locales from './resources/locales.json';

export const defaultLocale = 'en';
export const fallbackDefaultLangLocale = 'en-GB';
const typedLocales = locales as ContinentItem[];
const PIWIK_ID_WHITE = '632a67bb-026d-4e78-af5e-e4f960a7413a';

// list of all locales that we want to support in our application.
export const supportedLocales: string[] = typedLocales.flatMap(continent =>
  continent.countries.flatMap(country => country.languages.map(language => language.code))
);

// map of fallbacks pointing to a list of targets
// key = fallback -> list = locales which will fallback to its key
export const fallbackLocaleMap: Record<string, string[]> = {
  'en-GB': ['en'],
  'en-US': ['en-CA'],
  'de-DE': ['de-AT', 'de-CH', 'de-BE'],
  'es-ES': ['es-AR', 'es-CL', 'es-MX', 'es'],
  'fr-FR': ['fr-BE', 'fr-CA', 'fr-CH', 'fr-TN', 'fr'],
  'it-IT': ['it-CH'],
  'nl-NL': ['nl-BE'],
  'pt-PT': ['pt-BR'],
  'zh-CN': ['zh-TW'],
};

/**
 * A locale context is used for overrides of our base locales for systems like
 * MMA, Akeneo, or Storyblok where the locale is different to ours. The naming
 * of the context refers to the system where the different locales originate from.
 */
export enum LocaleContext {
  MMA = 'mma',
  Storyblok = 'storyblok',
  Akeneo = 'akeneo',
  Overstock = 'overstock',
  Default = '',
}

const localeOverrides = locales
  .flatMap(section => [...section.countries])
  .flatMap(country => country.languages)
  .reduce(
    (overrides, lang) => {
      // eslint-disable-next-line no-param-reassign
      overrides[lang.code] = lang.overrides ?? {};
      return overrides;
    },
    {} as Record<string, Partial<Record<LocaleContext, string>>>
  );

export const getLocaleForContext = (locale: string, context: LocaleContext): string => {
  if (context === LocaleContext.Default) {
    return locale;
  }

  const localeData = localeOverrides[locale];

  if (localeData && context in localeData && !!localeData[context]) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return localeData[context]!;
  }

  return locale;
};

const countryCodeOverrides = typedLocales
  .flatMap(continent => continent.countries)
  .reduce(
    (overrides, country) => {
      if (country.countryCode) {
        // eslint-disable-next-line no-param-reassign
        overrides[country.countryCode] = country.overrides ?? {};
      }

      return overrides;
    },
    {} as Record<string, Record<string, string>>
  );

/**
 * Resolves a context specific override for a country code. Unlike
 * `getLocaleForContext`, which works on locales such as "en-GB", this function
 * expects a plain ISO country code such as "GB" and returns the country code
 * the given system expects, for example "UK" for the overstock context.
 */
export const getCountryCodeForContext = (countryCode: string, context: LocaleContext): string => {
  if (context === LocaleContext.Default) {
    return countryCode;
  }

  return countryCodeOverrides[countryCode]?.[context] || countryCode;
};

export const localizeTranslations = (translations: Translations, localizations: Localizations): Translations => {
  if (
    typeof translations !== 'object' ||
    translations === null ||
    typeof localizations !== 'object' ||
    localizations === null
  ) {
    return localizations as Translations;
  }

  const result = { ...translations } as Record<string, string | Translations>;

  for (const key of Object.keys(localizations)) {
    const baseValue = translations[key];
    const overrideValue = localizations[key];

    if ((typeof baseValue === 'string' || typeof baseValue === 'undefined') && typeof overrideValue === 'string') {
      result[key] = overrideValue;
    } else if (
      (typeof baseValue === 'object' || typeof baseValue === 'undefined') &&
      typeof overrideValue === 'object'
    ) {
      result[key] = localizeTranslations(baseValue, overrideValue);
    }
  }

  return result;
};

export const generateFallbackLng = (initialLocales: string[]): Record<string, string[]> => {
  // create a copy of initialLocales, make sure default locale is present
  const localesWithDefault = initialLocales.includes(fallbackDefaultLangLocale)
    ? [...initialLocales]
    : [...initialLocales, fallbackDefaultLangLocale];

  // build reverse lookup from the const
  const reverseMap = Object.entries(fallbackLocaleMap).reduce<Record<string, string>>((acc, [target, sources]) => {
    sources.forEach(source => {
      acc[source] = target;
    });
    return acc;
  }, {});

  return localesWithDefault.reduce<Record<string, string[]>>((result, locale) => {
    // default locale has no fallback
    if (locale === fallbackDefaultLangLocale) {
      result[locale] = []; // eslint-disable-line no-param-reassign

      return result;
    }

    const fallbackChain: string[] = [];

    // mapped fallback (e.g. de-AT -> de-DE)
    const mapped = reverseMap[locale];
    if (
      mapped &&
      mapped !== locale &&
      (hubTranslations[mapped] || catalogTranslations[mapped]) &&
      !fallbackChain.includes(mapped)
    ) {
      fallbackChain.push(mapped);
    }

    // always fallback to default
    if (!fallbackChain.includes(fallbackDefaultLangLocale)) {
      fallbackChain.push(fallbackDefaultLangLocale);
    }

    result[locale] = fallbackChain; // eslint-disable-line no-param-reassign

    return result;
  }, {});
};

/**
 * returns a list of countryItems that are released
 * @param getDomainsReleasedForDev set true if domains that are released for dev / testing environments should be returned as well
 */
export const getReleasedCountries = (envName: string) =>
  typedLocales.flatMap(continent => [
    ...continent.countries.filter(country => {
      switch (envName) {
        case 'local':
          return true;
        case 'black':
          return false;
        case 'white':
          return country.isWebtestReleased ?? country.isReleased;
        case 'orange':
        default:
          return country.isReleased;
      }
    }),
  ]);

/**
 * returns a list of domains that is released
 * @param getDomainsReleasedForDev set true if domains that are released for dev / testing environments should be returned as well
 */
export const getReleasedDomains = (envName: string) => {
  const countries = getReleasedCountries(envName);
  const domainsWithDuplicates = countries.map(country => country.baseUrl);
  // remove duplicates from domainsWithDuplicates
  const domains = domainsWithDuplicates.filter((domain, index) => domainsWithDuplicates.indexOf(domain) === index);
  return domains;
};

export const getCountriesForDomain = (domain: string) =>
  typedLocales.flatMap(continent => continent.countries.filter(country => country.baseUrl === domain));

export const getDefaultLocaleForDomain = (domain: string, context: LocaleContext = LocaleContext.Default) => {
  const countriesWithDomain = getCountriesForDomain(domain);

  if (countriesWithDomain.length === 1) {
    // if the domain is used for a country with multiple languages (e.g. Switzerland, Belgium), use the defaultLocale attribute
    if (countriesWithDomain[0].languages.length > 1) {
      return countriesWithDomain[0].defaultLocale || defaultLocale;
    }
    // if the domain is used for a country with only one language (e.g. Germany, France), use the languages code
    return countriesWithDomain[0].languages[0].code;
  }
  // in all other cases (e.g. multiple countries for same domain like in igus.eu) use en
  return getLocaleForContext(defaultLocale, context);
};

export const getLocalesForDomain = (domain: string, context: LocaleContext = LocaleContext.Default) =>
  getCountriesForDomain(domain)
    .flatMap(country => country.languages.map(language => language.code))
    .filter(locale => locale !== getDefaultLocaleForDomain(domain))
    .map(locale => getLocaleForContext(locale, context));

export const getCountryForLocale = (locale: string): CountryItem | undefined =>
  typedLocales.flatMap(continent =>
    continent.countries.filter((country: CountryItem) => country.languages.some(language => language.code === locale))
  )[0] || undefined;

/**
 * Retrieves the base URL and available language locales for a given country.
 *
 * @param {string | undefined} countryCode - The ISO country code used to identify
 * the country. If countryCode is undefined, the returned locales fall back to the
 * locales available for igus.eu. This is because igus.eu is defined as the only
 * "country" without a countryCode in locales.json.
 * @param {LocaleContext} context - The optional context for the function call to
 * apply context-specific overrides, for example when using the locale for MMA API
 * requests or Storyblok. The default context is "LocaleContext.Default" which
 * does not apply any override.
 *
 * @returns {{ baseUrl: string, locales: string[] }} An object containing:
 * - `baseUrl` (string): The base URL for the given country or an empty string if
 *                       no country is found.
 * - `locales` (string[]): A list of language codes associated with the country or
 *                         an empty array if no languages are found.
 */

export const getLocalesForCountry = (
  countryCode: string | undefined,
  context: LocaleContext = LocaleContext.Default
): { baseUrl: string; locales: string[] } => {
  const country = typedLocales
    .flatMap(continent => continent.countries)
    .find(countryItem => countryItem.countryCode === countryCode);

  return {
    baseUrl: country?.baseUrl ?? '',
    locales: country?.languages.map(language => getLocaleForContext(language.code, context)) ?? [],
  };
};

export const isGtmEnabledForLocale = (locale: string): boolean => getCountryForLocale(locale)?.isGtmEnabled ?? false;

export const isJentisEnabledForLocale = (locale: string): boolean =>
  getCountryForLocale(locale)?.isJentisEnabled ?? false;

export const getPiwikId = (envName: string, locale: string): string | undefined => {
  switch (envName) {
    case 'white':
      return PIWIK_ID_WHITE;
    case 'orange':
      return getCountryForLocale(locale)?.piwikId ?? undefined;
    default:
      return undefined;
  }
};

export const isVATEnabledForLocale = (locale: string): boolean => getCountryForLocale(locale)?.isVATDisplayed ?? false;

export const getDomainsWithMultipleLanguages = (): string[] =>
  typedLocales.reduce<string[]>((acc, region) => {
    const multiLanguageDomains = region.countries.reduce<string[]>((countryAcc, country) => {
      if (country.languages.length > 1) {
        countryAcc.push(country.baseUrl.replace('www.', ''));
      }
      return countryAcc;
    }, []);
    return acc.concat(multiLanguageDomains);
  }, []);

/**
 * Creates a number formatter with consistent locale-specific formatting
 * across server (Node.js) and client (browser) environments.
 *
 * Some locales are resolved differently depending on the ICU/CLDR data
 * available in each environment, potentially causing formatting differences
 * and React hydration mismatches.
 */

const localeNumberFormatOverrides: Record<string, string> = {
  'en-LT': 'lt-LT',
  'en-EE': 'et-EE',
  'en-NO': 'nn-NO',
};

export const createNumberFormatter = (locale: string, options?: Intl.NumberFormatOptions): Intl.NumberFormat => {
  const resolvedLocale = localeNumberFormatOverrides[locale] ?? locale;

  return new Intl.NumberFormat(resolvedLocale, options);
};
